/**
 * Why a zone shape is refused, in words a producer reads — one clause per
 * rejection code, the same whether validateZonePolygon said no before the
 * write or the festival_areas_validate_polygon trigger did after it. Shared
 * by the web zone editor (its toasts, apps/web/src/lib/zoneWriteError.ts)
 * and the producer app's zone editor, so both say the same sentence.
 */

import {
  ZONE_MIN_AREA_SQUARE_METERS,
  ZONE_POLYGON_MAX_POSITIONS,
  ZONE_POLYGON_MIN_POSITIONS,
  maxZoneAreaSquareMeters,
  type ZonePolygonRejection,
} from "./polygonValidity";
import type { ZoneAreaType } from "../zonePalette";

/**
 * Why the shape is refused, as a clause ("its outline crosses itself").
 * `null`: the server refused it without a reason this client knows.
 */
export function zoneShapeProblem(
  reason: ZonePolygonRejection | null,
  areaType: ZoneAreaType,
): string {
  switch (reason) {
    case "not-a-polygon":
      return "it is not a single closed outline";
    case "not-closed":
      return "its outline is not closed";
    case "too-few-corners":
      return `it needs at least ${ZONE_POLYGON_MIN_POSITIONS - 1} corners`;
    case "too-many-corners":
      return `it has more than ${ZONE_POLYGON_MAX_POSITIONS - 1} corners`;
    case "self-intersecting":
      return "its outline crosses itself";
    case "too-small":
      return `it is smaller than ${ZONE_MIN_AREA_SQUARE_METERS} m²`;
    case "too-large":
      return `it is larger than ${maxZoneAreaSquareMeters(areaType) / 1_000_000} km²`;
    default:
      return "the shape is not valid";
  }
}
